import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, subDays } from 'date-fns';
import {
  Calendar,
  Filter,
  Hash,
  Layers,
  Server,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { clusterManagementService } from '../../services/clusterManagementService';
import { useIsMobile } from '../../hooks/useIsMobile';
import { GLOBAL_CLUSTER_ID, ShopChangelogFilters as Filters } from '../../types/shopChangelog';
import { cn } from '../../utils/cn';

interface ShopChangelogFiltersProps {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
  defaultFrom: Date;
}

/**
 * Filter bar mirrored from the existing HistoricCommandLogView pattern.
 * The inner TokenInput / Combobox / TimeRangeSelector primitives are copied
 * locally rather than refactored out — see PLAN.md §13.5 acceptance criterion.
 */
export function ShopChangelogFilters({ filters, onChange, defaultFrom }: ShopChangelogFiltersProps) {
  const isMobile = useIsMobile();
  const [clusterMap, setClusterMap] = useState<Record<string, string[]>>({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    clusterManagementService
      .getClusterFqdnMapping()
      .then((m) => {
        if (!cancelled) setClusterMap(m);
      })
      .catch(() => {
        // The combobox just shows whatever it has plus the GLOBAL pin.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clusterOptions = useMemo(() => {
    const real = Object.keys(clusterMap).sort((a, b) => a.localeCompare(b));
    // _GLOBAL_ is pinned to the top so it's always findable, even if the
    // cluster map hasn't loaded yet.
    return [GLOBAL_CLUSTER_ID, ...real];
  }, [clusterMap]);

  const activeFilterCount =
    filters.networkIds.length +
    filters.clusterIds.length +
    filters.userIds.length +
    filters.affectedPageIds.length +
    (Math.abs(filters.dateRange.from.getTime() - defaultFrom.getTime()) > 60_000 ? 1 : 0) +
    (filters.destructiveOnly ? 1 : 0);

  if (isMobile) {
    return (
      <div className="p-2 space-y-2">
        <div className="flex gap-2">
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="glass-button-sm flex-1 flex items-center justify-center gap-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-accent-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <DestructiveToggle
            value={filters.destructiveOnly}
            onChange={(v) => onChange({ destructiveOnly: v })}
          />
        </div>
        {filtersExpanded && (
          <div className="grid grid-cols-1 gap-2">
            <TokenInput
              value={filters.networkIds}
              onChange={(v) => onChange({ networkIds: v })}
              placeholder="Network ID..."
              icon={Hash}
            />
            <Combobox
              options={clusterOptions}
              selected={filters.clusterIds}
              onChange={(v) => onChange({ clusterIds: v })}
              placeholder="Filter clusters..."
              icon={Layers}
            />
            <TokenInput
              value={filters.userIds}
              onChange={(v) => onChange({ userIds: v })}
              placeholder="User ID (UUID or Discord ID)..."
              icon={User}
            />
            <TokenInput
              value={filters.affectedPageIds}
              onChange={(v) => onChange({ affectedPageIds: v })}
              placeholder="Affected page ID..."
              icon={Server}
            />
            <TimeRangeSelector
              value={filters.dateRange}
              onChange={(v) => onChange({ dateRange: v })}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-2 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
        <Combobox
          options={clusterOptions}
          selected={filters.clusterIds}
          onChange={(v) => onChange({ clusterIds: v })}
          placeholder="Filter clusters..."
          icon={Layers}
        />
        <TokenInput
          value={filters.userIds}
          onChange={(v) => onChange({ userIds: v })}
          placeholder="User ID (UUID or Discord ID)..."
          icon={User}
        />
        <TokenInput
          value={filters.affectedPageIds}
          onChange={(v) => onChange({ affectedPageIds: v })}
          placeholder="Page ID..."
          icon={Server}
        />
        <TokenInput
          value={filters.networkIds}
          onChange={(v) => onChange({ networkIds: v })}
          placeholder="Network ID..."
          icon={Hash}
        />
        <TimeRangeSelector
          value={filters.dateRange}
          onChange={(v) => onChange({ dateRange: v })}
        />
      </div>
      <div className="flex justify-between items-center pt-1">
        <DestructiveToggle
          value={filters.destructiveOnly}
          onChange={(v) => onChange({ destructiveOnly: v })}
        />
        {activeFilterCount > 0 && (
          <span className="text-xs text-glass-400">
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active
          </span>
        )}
      </div>
    </div>
  );
}

function DestructiveToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={cn(
        'glass-button-sm flex items-center gap-1.5 transition-all',
        value && 'bg-red-500/20 border border-red-500/50 text-red-200 ring-1 ring-red-500/40',
      )}
      title="Show only pushes that removed pages or entries"
    >
      <Trash2 className="w-3.5 h-3.5" />
      <span>Destructive only</span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Local primitives (copied from HistoricCommandLogView; see PLAN.md §13.5)
// ────────────────────────────────────────────────────────────────────────────

interface TokenInputProps {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  icon: React.ComponentType<{ className?: string }>;
}

function TokenInput({ value, onChange, placeholder, icon: Icon }: TokenInputProps) {
  const [inputValue, setInputValue] = useState('');

  const commit = () => {
    const v = inputValue.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !inputValue && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="glass-input flex items-center flex-wrap gap-1 p-1.5">
      <Icon className="w-4 h-4 text-glass-400 shrink-0 ml-1" />
      {value.map((token) => (
        <div
          key={token}
          className="flex items-center bg-accent-500/30 text-accent-300 text-xs font-semibold px-2 py-0.5 rounded-full"
        >
          <span className="font-mono truncate max-w-[180px]" title={token}>
            {token}
          </span>
          <button
            type="button"
            onClick={() => onChange(value.filter((t) => t !== token))}
            className="ml-1.5 hover:text-white"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : ''}
        className="bg-transparent outline-none flex-grow text-sm placeholder-glass-400 min-w-[80px]"
      />
    </div>
  );
}

interface ComboboxProps {
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  icon?: React.ComponentType<{ className?: string }>;
}

function Combobox({ options, selected, onChange, placeholder, icon: Icon }: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(
    () => options.filter((o) => o.toLowerCase().includes(inputValue.toLowerCase()) && !selected.includes(o)),
    [options, inputValue, selected],
  );

  const select = (option: string) => {
    if (!selected.includes(option)) onChange([...selected, option]);
    setInputValue('');
    setIsOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div
        className="glass-input flex items-center flex-wrap gap-1 p-1.5"
        onClick={() => setIsOpen(true)}
      >
        {Icon && <Icon className="w-4 h-4 text-glass-400 shrink-0 ml-1" />}
        {selected.map((token) => (
          <div
            key={token}
            className={cn(
              'flex items-center text-xs font-semibold px-2 py-0.5 rounded-full',
              token === GLOBAL_CLUSTER_ID
                ? 'bg-purple-500/30 text-purple-200'
                : 'bg-accent-500/30 text-accent-300',
            )}
          >
            <span>{token === GLOBAL_CLUSTER_ID ? 'GLOBAL' : token}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(selected.filter((s) => s !== token));
              }}
              className="ml-1.5 hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="bg-transparent outline-none flex-grow text-sm placeholder-glass-400 min-w-[80px]"
        />
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-1 glass-card max-h-60 overflow-y-auto"
          >
            {filtered.length > 0 ? (
              filtered.map((option) => (
                <div
                  key={option}
                  onClick={() => select(option)}
                  className={cn(
                    'px-3 py-2 text-sm cursor-pointer hover:bg-glass-700/80 flex items-center gap-2',
                    option === GLOBAL_CLUSTER_ID && 'border-b border-glass-200/10',
                  )}
                >
                  {option === GLOBAL_CLUSTER_ID ? (
                    <span className="text-purple-300 font-semibold">GLOBAL — applies to every cluster</span>
                  ) : (
                    <span>{option}</span>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-glass-400">No matches.</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface TimeRange {
  from: Date;
  to: Date;
}

interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (v: TimeRange) => void;
}

function TimeRangeSelector({ value, onChange }: TimeRangeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const presets: { label: string; from: Date; to: Date }[] = [
    { label: 'Last hour',    from: subDays(new Date(), 1 / 24), to: new Date() },
    { label: 'Last 24 hours', from: subDays(new Date(), 1),     to: new Date() },
    { label: 'Last 7 days',  from: subDays(new Date(), 7),      to: new Date() },
    { label: 'Last 30 days', from: subDays(new Date(), 30),     to: new Date() },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="glass-input w-full flex items-center justify-between text-left p-2"
      >
        <span className="truncate text-xs">
          {format(value.from, 'MMM d, HH:mm')} − {format(value.to, 'MMM d, HH:mm')}
        </span>
        <Calendar className="w-4 h-4 text-glass-400 shrink-0 ml-2" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-[300px] mt-1 glass-card p-3 space-y-3 right-0"
          >
            <div className="grid grid-cols-2 gap-2">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    onChange({ from: p.from, to: p.to });
                    setIsOpen(false);
                  }}
                  className="glass-button-sm text-center"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs text-glass-300">From</label>
              <input
                type="datetime-local"
                value={format(value.from, "yyyy-MM-dd'T'HH:mm")}
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  if (!isNaN(d.getTime())) onChange({ ...value, from: d });
                }}
                className="glass-input w-full mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-glass-300">To</label>
              <input
                type="datetime-local"
                value={format(value.to, "yyyy-MM-dd'T'HH:mm")}
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  if (!isNaN(d.getTime())) onChange({ ...value, to: d });
                }}
                className="glass-input w-full mt-1"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
